import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';
import { screenFromUrl } from './where-am-i.tool';
import { ShowHighlightService, ShowTarget, KNOWN_AREAS, KnownArea } from './show-highlight.service';

export { KNOWN_AREAS };
export type { KnownArea };

/**
 * Dependencies the show tool needs from the editor at runtime.
 * The spec provides stubs; the real wiring injects Angular services.
 */
export interface ShowDeps {
  /** Return the list of component ids on the currently open page, or null if not on the page editor. */
  getPageComponentIds: () => string[] | null;
  /** Return the list of field names on the currently open entity, or null if not on an entity. */
  getEntityFieldNames: () => string[] | null;
  /** The highlight service that stores the overlay state. */
  highlightService: ShowHighlightService;
  /** The element the overlay would ring for this target, or null when none is drawn. */
  findElement: (target: ShowTarget) => Element | null;
  /** How long to wait for the element to render; defaults to SHOW_SETTLE_MS. */
  settleMs?: number;
}

/** Long enough for a screen navigate just opened to draw, short enough not to stall the agent. */
export const SHOW_SETTLE_MS = 1000;
const SETTLE_POLL_MS = 100;

async function waitForElement(deps: ShowDeps, target: ShowTarget): Promise<boolean> {
  const deadline = Date.now() + (deps.settleMs ?? SHOW_SETTLE_MS);
  for (;;) {
    if (deps.findElement(target)) return true;
    if (Date.now() >= deadline) return false;
    await new Promise((r) => setTimeout(r, SETTLE_POLL_MS));
  }
}

const refuse = (refusal: string): ToolAnswer => ({ outcome: 'refused', refusal });
const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
const AREA_LIST = `${KNOWN_AREAS.slice(0, -1).join(', ')} and ${KNOWN_AREAS[KNOWN_AREAS.length - 1]}`;

// Each refusal says which screen the target lives on, or what the screen does hold, so the agent's next
// call can be the right one.
function targetProblem(target: ShowTarget, screen: string, deps: ShowDeps): string | null {
  switch (target.kind) {
    case 'component': {
      if (screen !== 'page-editor') {
        return `Components are shown on the page editor, and the builder's screen is ${screen}. Use navigate to open the page first.`;
      }
      const ids = deps.getPageComponentIds();
      return ids?.includes(target.id) ? null : `The open page has no component '${target.id}'. Check the id with the builder.`;
    }
    case 'field': {
      if (screen !== 'entity') {
        return `Fields are shown on an entity's screen, and the builder's screen is ${screen}. Use navigate to open the entity first.`;
      }
      const names = deps.getEntityFieldNames();
      if (!names) return 'No entity is open on the builder\'s screen. Use navigate to open the entity first.';
      return names.includes(target.id) ? null : `The open entity has no field '${target.id}'. Its fields are: ${names.join(', ')}.`;
    }
    case 'area':
      return KNOWN_AREAS.includes(target.id as KnownArea) ? null : `There's no area called '${target.id}'. The areas are ${AREA_LIST}.`;
    case 'button':
      return 'Buttons aren\'t a target for show. A confirm-lane action marks the button the builder has to press.';
  }
}

function describeTarget(target: ShowTarget): string {
  switch (target.kind) {
    case 'component': return `component '${target.id}'`;
    case 'field': return `field '${target.id}'`;
    case 'area': return `area '${target.id}'`;
    case 'button': return `button '${target.id}'`;
  }
}

export function makeShowTool(deps: ShowDeps): ToolDef {
  return {
    name: 'show',
    kind: 'live',
    description: [
      'Draws a ring around one thing on the current screen, with an optional short note: a field on the',
      'open entity (by field name), a component on the open page (by component id), or an area',
      `(${KNOWN_AREAS.slice(0, -1).join(', ')} or ${KNOWN_AREAS[KNOWN_AREAS.length - 1]}).`,
      'Use it to point the builder at what you are about to change or just changed.',
      'One highlight at a time; it clears after 6 seconds or when the builder clicks.',
      'It won\'t look beyond the current screen, so navigate first. It changes nothing.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        target: {
          type: 'object',
          description: 'What to highlight.',
          properties: {
            kind: {
              type: 'string',
              enum: ['component', 'field', 'area'],
              description: 'The kind of target: component on the page, field on the entity, or a screen area.',
            },
            id: {
              type: 'string',
              description: 'The component id, field name, or area name to highlight.',
            },
          },
          required: ['kind', 'id'],
          additionalProperties: false,
        },
        note: {
          type: 'string',
          description: 'Optional short note displayed on the overlay.',
        },
      },
      required: ['target'],
      additionalProperties: false,
    },

    async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const target = input['target'] as ShowTarget;
      const note = input['note'] as string | undefined;
      const problem = targetProblem(target, screenFromUrl(ctx.routerUrl), deps);
      if (problem) return refuse(problem);

      // The data can list a target the screen never draws (an untagged kind, another graph engine);
      // saying "Highlighted" then would tell the agent the builder saw a ring that isn't there.
      if (!(await waitForElement(deps, target))) {
        return refuse(`${capitalize(describeTarget(target))} isn't drawn on the screen right now, so there's nothing to ring. If it belongs to another screen, navigate there first.`);
      }

      const showTarget: ShowTarget = { kind: target.kind, id: target.id };
      deps.highlightService.highlight(showTarget, note);

      const result: Record<string, unknown> = { target: showTarget };
      if (note) result['note'] = note;

      return {
        outcome: 'done',
        changed: [`Highlighted ${describeTarget(showTarget)}`],
        result,
      };
    },
  };
}
